// Angular imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Constants imports
import { CacheDuration } from '../../_constants/cache-duration';

// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// Model imports
import { ChangeDocumentOwnerEditorResultDto } from '../_models/change-document-owner/change-document-owner-editor-result-dto';
import { ChangeDocumentOwnerRawDto } from '../_models/change-document-owner/change-document-owner-raw-dto';
import { DocumentHistoryResultDto } from '../_models/document-history/document-history-result-dto';
import { DocumentTreeResultDto } from '../_models/document-tree-result-dto';
import { DocumentParameterDto } from '../_models/document-parameter-dto';
import { DocumentResultDto } from '../_models/document-result-dto';
import { RenameDocumentParamertersDto } from '../_models/rename-document-parameters-dto';
import { BaseDocumentParametersDto } from '../_models/base-document-parameters-dto';
import { DocumentEditorParameterDto } from '../_models/documents-editor/document-editor-parameter-dto';
import { DocumentEditorResultDto } from '../_models/documents-editor/document-editor-result-dto';
import { DocumentRawDto } from '../_models/documents-editor/document-raw-dto';
import { DocumentHistoryParametersDto } from '../_models/document-history/document-history-parameters-dto';
import { PrintingCertifiedResultDto } from '../_models/track-certified/printing-certified-result-dto';
import { UpdatePrintingCertifiedTrackingDetailsParametersDto } from '../_models/track-certified/update-printing-certified-tracking-details-parameters-dto';
import { DocumentExistResultDto } from '../_models/document-exist-result-dto';
import { DocumentAddToResalePackageDto } from '../_models/document-add-to-resale-package-dto';

@Injectable({
    providedIn: 'root'
})
export class DocumentService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getDocumentTree(
        baseApiUrl: string,
        data: BaseDocumentParametersDto,
        readOnly = false
    ): Observable<DocumentTreeResultDto[]> {
        const url = `${baseApiUrl}documents/tree${readOnly ? '/rp' : ''}${data.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, data.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<DocumentTreeResultDto[]>(url, options)
            .pipe(map((responseData: DocumentTreeResultDto[]) => plainToClass(DocumentTreeResultDto, responseData)));
    }

    getDocumentList(baseApiUrl: string, data: DocumentParameterDto, readOnly = false): Observable<DocumentResultDto[]> {
        const url = `${baseApiUrl}documents/list${readOnly ? '/rp' : ''}${data.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, data.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<DocumentResultDto[]>(url, options)
            .pipe(map((responseData: DocumentResultDto[]) => plainToClass(DocumentResultDto, responseData)));
    }

    downloadFile(fileUrl: string): Observable<Blob> {
        return this.httpClient
            .get(fileUrl, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    // Rename Document name
    renameDocumentName(baseApiUrl: string, param: RenameDocumentParamertersDto): Observable<any> {
        const url = `${baseApiUrl}document/rename`;
        return this.httpClient.put(url, classToPlain(param)).pipe(map((responseData: any) => responseData));
    }

    // Delete Document
    deleteDocument(baseApiUrl: string, param: BaseDocumentParametersDto): Observable<any> {
        const url = `${baseApiUrl}document/delete`;
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: { communityId: param.communityId, fileId: param.fileId }
        };
        return this.httpClient.delete<any>(url, options);
    }

    // change document owner editor
    getChangeOwnerEditor(
        baseApiUrl: string,
        param: BaseDocumentParametersDto
    ): Observable<ChangeDocumentOwnerEditorResultDto> {
        const url = `${baseApiUrl}document/change/owner/editor${param.getQueryString()}`;
        return this.httpClient
            .get<ChangeDocumentOwnerEditorResultDto>(url)
            .pipe(
                map((responseData: ChangeDocumentOwnerEditorResultDto) =>
                    plainToClass(ChangeDocumentOwnerEditorResultDto, responseData)
                )
            );
    }

    changeOwnerFromDocument(baseApiUrl: string, param: ChangeDocumentOwnerRawDto): Observable<any> {
        const url = `${baseApiUrl}document/change/owner`;
        return this.httpClient.put(url, classToPlain(param)).pipe(map((responseData: any) => responseData));
    }

    // Document Editor
    getDocumentEditor(baseApiUrl: string, data: DocumentEditorParameterDto): Observable<DocumentEditorResultDto> {
        const url = `${baseApiUrl}document/editor${data.getQueryString()}`;
        return this.httpClient
            .get<DocumentEditorResultDto>(url)
            .pipe(map((responseData: DocumentEditorResultDto) => plainToClass(DocumentEditorResultDto, responseData)));
    }

    uploadNewDocument(baseApiUrl: string, data: DocumentRawDto): Observable<any> {
        return this.httpClient
            .post(`${baseApiUrl}document`, classToPlain(data))
            .pipe(map((responseData: any) => responseData));
    }

    updateExistingDocument(baseApiUrl: string, data: DocumentRawDto): Observable<any> {
        return this.httpClient
            .put(`${baseApiUrl}document`, classToPlain(data))
            .pipe(map((responseData: any) => responseData));
    }

    // Document History
    getDocumentHistory(baseApiUrl: string, data: DocumentHistoryParametersDto): Observable<DocumentHistoryResultDto[]> {
        const url = `${baseApiUrl}document/history${data.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, data.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<DocumentHistoryResultDto[]>(url, options)
            .pipe(
                map((responseData: DocumentHistoryResultDto[]) => plainToClass(DocumentHistoryResultDto, responseData))
            );
    }

    // Track Certified
    getCertifiedDetails(
        baseApiUrl: string,
        data: DocumentHistoryParametersDto
    ): Observable<PrintingCertifiedResultDto[]> {
        const url = `${baseApiUrl}document/printing/certified-details${data.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, data.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<PrintingCertifiedResultDto[]>(url, options)
            .pipe(
                map((responseData: PrintingCertifiedResultDto[]) =>
                    plainToClass(PrintingCertifiedResultDto, responseData)
                )
            );
    }

    getPrintingCertifiedDocument(baseApiUrl: string, param: BaseDocumentParametersDto): Observable<Blob> {
        const url = `${baseApiUrl}document/tracking${param.getQueryString()}`;

        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    updateTrackingDetails(
        baseApiUrl: string,
        data: UpdatePrintingCertifiedTrackingDetailsParametersDto
    ): Observable<any> {
        return this.httpClient.put(`${baseApiUrl}document/printing/certified/tracking`, classToPlain(data));
    }

    addToResalePackage(baseApiUrl: string, param: DocumentAddToResalePackageDto): Observable<DocumentExistResultDto> {
        const url = `${baseApiUrl}document/addtoresalepackage`;
        return this.httpClient
            .put(url, classToPlain(param))
            .pipe(map((response: any) => plainToClass(DocumentExistResultDto, response)));
    }

    getDocumentsByFileName(
        baseApiUrl: string,
        data: DocumentParameterDto,
        readOnly: boolean
    ): Observable<DocumentResultDto[]> {
        const url = `${baseApiUrl}documents/search${readOnly ? `/rp` : ``}${data.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((response: any[]) => plainToClass(DocumentResultDto, response)));
    }
}
